import React from 'react';
import { Helmet } from 'react-helmet-async';
import { isProduction } from 'scripts/application/setting';

const aiModeHost = isProduction ? 'aimode.har.com' : 'aimode-test.har.com';
const defaultAiModeBaseUrl = `https://${aiModeHost}`;
const defaultOgImage = `${defaultAiModeBaseUrl}/static/images/ai-og-image.png`;

interface MetaHeadProps {
    title?: string;
    description?: string;
    keywords?: string;
    url?: string;
    image?: string;
    imageAlt?: string;
    type?: 'website' | 'article';
    robots?: string;
}

export const MetaHead: React.FC<MetaHeadProps> = ({
    title = "AI Mode – Discover Homes with HAR.com's AI-powered Search",
    description = "HAR.com's AI Mode helps you find the perfect home with personalized recommendations and advanced AI-powered search. Explore today and find your dream home!",
    keywords = "AI-powered home search, HAR.com AI search, AI real estate tools, AI home recommendations, AI in real estate search, Home search with AI",
    url = `${defaultAiModeBaseUrl}/`,
    image = defaultOgImage,
    imageAlt,
    type = "website",
    robots = 'index, follow',
}) => {
    const resolvedImageAlt = imageAlt ?? title;

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />

            {/* Open Graph Meta Tags */}
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={type} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={image} />
            <meta property="og:image:alt" content={resolvedImageAlt} />
            <meta property="og:site_name" content="HAR.com" />
            <meta property="og:locale" content="en_US" />

            {/* Twitter Meta Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@HAR_com" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:image:alt" content={resolvedImageAlt} />

            {/* Canonical URL */}
            <link rel="canonical" href={url} />
            <meta name="robots" content={robots} />
        </Helmet>
    );
};